import { BASE_FRONTEND_URL } from './config/config'

const POSTHOG_API_KEY = 'phx_JUt42IMaiym4Gc12r3A9rLfKtk4j7RBo3Qbt983UR9H7zIM'
const POSTHOG_PROJECT_ID = '26198'
const POSTHOG_API_BASE = 'https://us.posthog.com'

export type PosthogPath = {
  title: string
  path: string
}

/**
 * Fetch top 10 visited paths with visit count classification
 * @param {string} [userEmail] - Optional user email to filter by
 * @param {string} [userId] - Optional PostHog distinct_id to filter by
 * @returns {Promise<Array>} - Sorted array of { path, visits, classification }
 */
export async function getTopVisitedPaths(userEmail: string, userId: string = null): Promise<PosthogPath[]> {
  const headers = {
    Authorization: `Bearer ${POSTHOG_API_KEY}`,
    'Content-Type': 'application/json'
  }

  // Build HogQL query to get top visited paths
  const hogqlQuery = `
    SELECT
      properties.title as title,
      properties.$current_url as path,
      count() as visits
    FROM events
    WHERE
      event = '$pageview' AND
      distinct_id = '${userEmail}' AND
      properties.$current_url LIKE '${BASE_FRONTEND_URL}%' AND
      properties.$current_url NOT LIKE '${BASE_FRONTEND_URL}/' AND
      properties.$current_url NOT LIKE '${BASE_FRONTEND_URL}'
    GROUP BY title,path
    ORDER BY visits DESC
    LIMIT 10
  `

  try {
    const response = await fetch(`${POSTHOG_API_BASE}/api/projects/${POSTHOG_PROJECT_ID}/query/`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query: {
          kind: 'HogQLQuery',
          query: hogqlQuery
        }
      })
    })

    const data = await response.json()
    const results = data.results || []

    // Process and classify results
    return results.map(([path, visits], index) => ({
      path: path.replace(BASE_FRONTEND_URL, '') || 'Unknown Path',
      visits: Number(visits) || 0,
      rank: index + 1
    }))
  } catch (error) {
    console.error('Error fetching paths:', error)
    return []
  }
}

/**
 * Fetch top 10 distinct recent paths
 * @param {string} userEmail - User email to filter by
 * @param {string} [userId] - Optional PostHog distinct_id to filter by
 * @returns {Promise<PosthogPath[]>} - Array of recent distinct paths
 */
export async function getRecentDistinctPaths(userEmail: string, userId: string = null): Promise<PosthogPath[]> {
  const headers = {
    Authorization: `Bearer ${POSTHOG_API_KEY}`,
    'Content-Type': 'application/json'
  }

  // Build HogQL query to get most recent distinct paths from the last 2 weeks
  // Use properties.$current_url directly and handle query params in post-processing
  const hogqlQuery = `
    SELECT
      properties.title as title,
      properties.$current_url as path,
      max(timestamp) as latest_visit,
      count() as visits
    FROM events
    WHERE
      event = '$pageview' AND
      distinct_id = '${userEmail}' AND
      properties.$current_url LIKE '${BASE_FRONTEND_URL}%' AND
      properties.$current_url NOT LIKE '${BASE_FRONTEND_URL}/' AND
      properties.$current_url NOT LIKE '${BASE_FRONTEND_URL}' AND
      properties.$current_url NOT LIKE '${BASE_FRONTEND_URL}/wizard%' AND
      properties.title NOT LIKE 'Loop' AND
      timestamp > now() - INTERVAL 14 DAY
    GROUP BY title, path
    ORDER BY latest_visit DESC
    LIMIT 10
  `

  try {
    const response = await fetch(`${POSTHOG_API_BASE}/api/projects/${POSTHOG_PROJECT_ID}/query/`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query: {
          kind: 'HogQLQuery',
          query: hogqlQuery
        }
      })
    })

    const data = await response.json()
    const results = data.results || []

    // Process results and strip query parameters in JavaScript
    const seenPaths = new Set()
    return results
      .map(([title, path]) => {
        if (!title || !path) return null

        // Remove query parameters here in JavaScript instead of in the SQL query
        const pathWithoutQuery = path.split('?')[0]
        const normalizedPath = pathWithoutQuery.replace(BASE_FRONTEND_URL, '')

        // Normalize the path - ensure consistent handling of leading slash
        const pathKey = normalizedPath.startsWith('/') ? normalizedPath : '/' + normalizedPath

        // Skip if we've seen this path before
        if (seenPaths.has(pathKey)) return null
        seenPaths.add(pathKey)

        return {
          title: title,
          path: normalizedPath
        }
      })
      .filter(Boolean)
  } catch (error) {
    console.error('Error fetching recent paths:', error)
    return []
  }
}

/**
 * Add a new route to the recent routes list
 * @param {PosthogPath[]} currentRoutes - Current list of recent routes
 * @param {string} title - Page title
 * @param {string} path - Route path
 * @returns {PosthogPath[]} - Updated list of recent routes
 */
export function updateRecentRoutes(currentRoutes: PosthogPath[], title: string, path: string): PosthogPath[] {
  // Make a copy of the current routes to avoid mutating the original
  const updatedRoutes = [...currentRoutes]

  // Strip any query parameters from the path
  const pathWithoutQuery = path.split('?')[0]

  // Create new route object
  const newRoute: PosthogPath = {
    title,
    path: pathWithoutQuery
  }

  // Find and remove any existing entry for the same path
  const existingIndex = updatedRoutes.findIndex((route) => route.path === pathWithoutQuery)
  if (existingIndex !== -1) {
    updatedRoutes.splice(existingIndex, 1)
  }

  // Add new route to the beginning of the array
  updatedRoutes.unshift(newRoute)

  // Limit to 10 items
  return updatedRoutes.slice(0, 10)
}
